import {
  Alert,
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Spin,
  Tag
} from 'antd';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import React, { useEffect, useState, useMemo } from 'react';
import { TextAreaWithCharacterCount } from '../../../../../components/TextAreaWithCharacterCount';
import { useBezorgmomenten } from '../../../../../core-module/hooks/useBezorgmomenten';
import moment from 'moment';
import { useActiveWeeklyMenus } from '../../../../../core-module/hooks/useActiveWeeklyMenu';
import { useWeeklyMenu } from '../../../../../core-module/hooks/useWeeklyMenu';
import {
  BezorgmomentResponse,
  CustomerInformationForBezorgmomentenResponse
} from '../../../../../core-module/services/deliverer/types';
import { useUpsertCartRequest } from '../../../../../core-module/hooks/useUpsertCartRequest';
import {
  hasMealWithMoreThanThreeDishesForSap,
  hasOrderValidAmountOfProducts
} from '../../../../../core-module/selectors/Order/selectors';
import Swal from 'sweetalert2';
import { FormProductValuesSap } from '../../../../../core-module/selectors/Order/types';
import { delivererClient } from '../../../../../core-module/services/deliverer/DelivererClient';

const dishLayout = {
  wrapperCol: {
    xs: { span: 24, offset: 0 }
  }
};

interface Props {
  readonly form: WrappedFormUtils;
  readonly customerInformation: CustomerInformationForBezorgmomentenResponse;
  readonly isSubmitting: boolean;
  readonly customerDefaultPortionSize: string | null;
  readonly selectedBezorgmoment: BezorgmomentResponse | null;
  readonly setSelectedBezorgmoment: (value: BezorgmomentResponse) => void;
  readonly onHideClick: () => void;
  readonly hasValidPaymentMethod: boolean;
}

const OrderFormComponent: React.FC<Props> = ({
  form,
  customerInformation,
  isSubmitting,
  customerDefaultPortionSize,
  selectedBezorgmoment,
  setSelectedBezorgmoment,
  onHideClick,
  hasValidPaymentMethod
}) => {
  const [defaultPortionSize, setDefaultPortionSize] = useState(customerDefaultPortionSize);
  const [canUpsertCart, setCanUpsertcart] = useState(false);
  const [cartId, setCartId] = useState<string | null>(null);
  const [voucherCode, setVoucherCode] = useState<string[] | null>([]);
  const [isUpserting, setIsUpserting] = useState(false);
  const [bezorgmomentWithExistingOrders, setBezorgmomentWithExistingOrders] = useState<string[]>(
    []
  );
  const { activeWeeklyMenus } = useActiveWeeklyMenus();

  const getPaperWeekMenuLabel = (deliveryMoment: string) =>
    activeWeeklyMenus.find((menu) =>
      moment(deliveryMoment).isBetween(menu.startDate, menu.finalDate, 'day', '[]')
    )?.paperWeekMenuLabel;

  const latestEndDate = useMemo(() => {
    if (!activeWeeklyMenus || activeWeeklyMenus.length === 0) {
      return undefined;
    }

    const latestTimestamp = Math.max(
      ...activeWeeklyMenus.map((menu) => new Date(menu.finalDate).getTime())
    );
    const date = new Date(latestTimestamp);
    date.setHours(23, 59, 59, 999);
    return date;
  }, [activeWeeklyMenus]);

  const { bezorgmomenten } = useBezorgmomenten(
    customerInformation.postalCode.replace(/\s+/g, ''),
    customerInformation.customerNumber,
    latestEndDate
  );

  const selectedBezorgMomentDate = form.getFieldValue('delivery_date');

  useEffect(() => {
    if (!customerInformation.customerNumber) {
      setBezorgmomentWithExistingOrders([]);
      return;
    }
    const existingOrderForCustomer = delivererClient.getOrdersByCustomer(
      customerInformation.customerNumber.toString(),
      1,
      30
    );

    existingOrderForCustomer.then((orders) => {
      setBezorgmomentWithExistingOrders(
        orders.items
          .map((order) =>
            order.deliveries.map((delivery) =>
              moment(delivery.requestedDeliveryMoment).toISOString(true)
            )
          )
          .flat()
      );
    });
  }, [selectedBezorgMomentDate, customerInformation.customerNumber, form]);

  const { weeklyMenu, loading, error: weeklyMenuError } = useWeeklyMenu(
    selectedBezorgMomentDate || undefined
  );
  const weeklyMenuItems = weeklyMenu?.items || [];
  const weeklyMenuBoxes = weeklyMenu?.boxes || [];
  // Combine items and boxes for rendering
  const allProducts = [...weeklyMenuItems, ...weeklyMenuBoxes];

  useEffect(() => {
    const selectedDeliveryDate = form.getFieldValue('delivery_date');
    if (selectedDeliveryDate && allProducts.length > 0) {
      setCanUpsertcart(true);
    } else {
      setCanUpsertcart(false);
    }
  }, [form, allProducts]);

  useEffect(() => {
    const selectedMoment = bezorgmomenten.find(
      (moment) => moment.startDeliveryMoment === selectedBezorgMomentDate
    );
    if (selectedMoment) {
      // if we change the delivery date, we reset the selected menu items so that the old items are no longer selected
      form.resetFields(
        Object.keys(form.getFieldsValue()).filter((key) => key.startsWith('weeklyMenuItems'))
      );
      // if we change the delivery date, we reset the route comments because the comment is per delivery
      form.resetFields(
        Object.keys(form.getFieldsValue()).filter((key) => key.startsWith('route_comments'))
      );
      setSelectedBezorgmoment(selectedMoment);
    }
    //we can't return form in this hook, because it will cause an infinite loop
    // eslint-disable-next-line
  }, [bezorgmomenten, selectedBezorgMomentDate, setSelectedBezorgmoment]);

  const handleUpsertCart = () => {
    form.validateFields(
      ['delivery_date', 'weeklyMenuItems', 'alternativeItems', 'route_comments', 'voucher_code'],
      async (errors, values) => {
        if (errors) {
          console.error('Validation errors:', errors);
          return;
        }
        runUpsertFlow(values);
      }
    );
  };
  // The upsert flow includes several checks and confirmations before actually upserting the cart
  const runUpsertFlow = async (values) => {
    // 1. Retreive existing orders and validate the order
    // we re-retrieve the existing orders here instead of in a useEffect hook because we need the latest data right before upserting the cart.
    const existingOrders = await delivererClient.getOrdersByCustomer(
      customerInformation.customerNumber?.toString() ?? '',
      1,
      20
    );

    const orderExists = existingOrders.items.some((order) =>
      order.deliveries.some(
        (delivery) =>
          moment(delivery.requestedDeliveryMoment).toISOString(true) ===
          moment(values.delivery_date).toISOString(true)
      )
    );

    const selectedWeeklyMenuItems = Object.values(values.weeklyMenuItems || {}).filter(
      (item): item is FormProductValuesSap => (item as FormProductValuesSap).selected
    );

    // Convert selected alternatives to the same format for validation
    const selectedAlternatives = Object.values(
      values.alternativeItems || {}
    ).flatMap((alternativeGroup) =>
      Object.values(alternativeGroup as any).filter((item: any) => item.selected)
    );

    // Convert alternatives to FormProductValuesSap format
    const alternativesAsMenuItems: FormProductValuesSap[] = selectedAlternatives.map((alt: any) => {
      // Check if it's a box alternative with portions or regular alternative with quantity
      if (alt.portions) {
        return {
          selected: alt.selected,
          category: alt.category,
          portions: alt.portions
        };
      } else {
        // Regular alternative with single quantity
        return {
          selected: alt.selected,
          category: alt.category,
          portions: {
            default: {
              productId: alt.productId,
              quantity: alt.quantity || 0
            }
          }
        };
      }
    });

    // Combine main items and alternatives for validation
    const allSelectedItems = [...selectedWeeklyMenuItems, ...alternativesAsMenuItems];

    const hasNotValidAmountOfProducts = !hasOrderValidAmountOfProducts(allSelectedItems);
    const hasLargeQuantity = hasMealWithMoreThanThreeDishesForSap(allSelectedItems);

    // 2. Check minimum number of products
    if (hasNotValidAmountOfProducts) {
      await Swal({
        title: 'Let op',
        html: `Er moeten minimaal 2 maaltijden of 1 maaltijdbox besteld worden op een bezorgmoment.`,
        type: 'warning',
        confirmButtonText: 'Ok'
      });
      return;
    }

    // 3. Check for existing order
    if (orderExists) {
      const result = await Swal({
        title: 'Let op',
        html: `Er bestaat al een bestelling voor dit bezorgmoment. Weet je zeker dat je deze bestelling wilt plaatsen?`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ja',
        cancelButtonText: 'Nee'
      });
      if (!result.value) {
        return;
      }
    }

    // 4. Check for large quantities
    if (hasLargeQuantity) {
      const result = await Swal({
        title: 'Let op',
        html: `Voor 1 of meer maaltijden is een aantal groter dan 3 ingevoerd. Weet je zeker dat je door wilt gaan?`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ja',
        cancelButtonText: 'Nee'
      });
      if (!result.value) {
        return;
      }
    }

    // 5. Upsert cart
    try {
      setIsUpserting(true);
      const completed = await upsertCart();
      if (completed) {
        onHideClick();
      }
    } finally {
      setIsUpserting(false);
    }
  };

  const { upsertCart, notifications } = useUpsertCartRequest({
    form,
    customerInformation,
    selectedBezorgmoment,
    cartId,
    setCartId,
    voucherCode,
    setVoucherCode
  });

  useEffect(() => {
    // Only clear the voucher_code field if there are notifications returned from upsertCart
    if (notifications && notifications.length > 0) {
      form.resetFields(
        Object.keys(form.getFieldsValue()).filter((key) => key.startsWith('voucher_code'))
      );
    }
  }, [notifications, form]);

  const upsertCartDisabled = !canUpsertCart || isUpserting || !hasValidPaymentMethod;

  return (
    <Form
      style={{ marginTop: 20 }}
      labelCol={{
        xs: { span: 24 },
        sm: { span: 4 }
      }}
      wrapperCol={{
        xs: { span: 24 },
        sm: { span: 10 }
      }}
    >
      <Row>
        <Col>
          <Button onClick={onHideClick}>Annuleren</Button>
          <Button
            style={{ float: 'right' }}
            disabled={upsertCartDisabled}
            loading={isSubmitting || isUpserting}
            type='primary'
            onClick={handleUpsertCart}
          >
            Bestellen
          </Button>
        </Col>
        {!hasValidPaymentMethod ? (
          <Alert
            style={{ display: 'block', marginTop: 15 }}
            type='warning'
            message='Kan geen orders plaatsen, de klant heeft geen geldige betaalmethode. Neem contact op met klantenservice om dit aan te passen.'
          />
        ) : null}
      </Row>
      <hr />
      <Row>
        <Col xs={12}>
          <Form.Item
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            label='Bezorgmoment'
            extra='Dagen waar al een bestelling voor gemaakt zijn uitgeschakeld. Voor wijzigingen aan een bestaande bestelling, bel de klantenservice.'
          >
            {form.getFieldDecorator('delivery_date', {
              rules: [{ required: true, message: 'Kies een bezorgmoment' }]
            })(
              <Select<number> placeholder='- Maak een keuze -'>
                {bezorgmomenten.map((bezorgmoment, index) => {
                  const paperWeekMenuLabel = getPaperWeekMenuLabel(
                    bezorgmoment.startDeliveryMoment
                  );
                  const weekMenuLabelSuffix = paperWeekMenuLabel?.trim()
                    ? ` | ${paperWeekMenuLabel}`
                    : '';

                  return (
                    <Select.Option
                      key={index}
                      value={bezorgmoment.startDeliveryMoment}
                      disabled={bezorgmomentWithExistingOrders.includes(
                        moment(bezorgmoment.startDeliveryMoment).toISOString(true)
                      )}
                    >
                      {`${moment(bezorgmoment.startDeliveryMoment).format(
                        'dd DD MMM'
                      )} tussen ${moment(bezorgmoment.startDeliveryMoment).format('HH:mm')} en
                        ${moment(bezorgmoment.endDeliveryMoment).format('HH:mm')}
                       ${weekMenuLabelSuffix}`}
                    </Select.Option>
                  );
                })}
              </Select>
            )}
          </Form.Item>
        </Col>
        <Col xs={12}>
          <Form.Item
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            label='Routeopmerking'
            extra={
              <p>
                Plaats hier alleen een eenmalige opmerking mbt dit bezorgmoment. Deze zal op de
                routelijst weergegeven worden. Voor overige vragen, mail ons op
                maaltijdservice@uitgekookt.nl. Er zijn maximaal 150 tekens in te voeren. Voor het
                plaatsen van een vaste route opmerking, ga naar het tabblad "Profiel".
              </p>
            }
          >
            {form.getFieldDecorator('route_comments', {
              rules: [
                { required: false },
                {
                  max: 150,
                  message: 'De maximale lengte is 150 karakters'
                }
              ]
            })(<TextAreaWithCharacterCount showCharacterCount rows={4} maxLength={150} />)}
          </Form.Item>
        </Col>
        <Col xs={12}>
          <Form.Item labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} label='Vouchercode'>
            {form.getFieldDecorator('voucher_code', {
              rules: [{ required: false }],
              initialValue: voucherCode
            })(<Input />)}
            {notifications &&
              notifications.length > 0 &&
              notifications.map((notification, index) => (
                <div style={{ color: 'red' }} key={index}>
                  {notification}
                </div>
              ))}
          </Form.Item>
        </Col>
      </Row>
      {selectedBezorgmoment !== null && loading && (
        <Row style={{ textAlign: 'center', marginTop: 20 }}>
          <Spin />
        </Row>
      )}
      {selectedBezorgmoment !== null && !loading && weeklyMenuError && (
        <Alert
          style={{ display: 'block', marginTop: 15, marginBottom: 15 }}
          type='warning'
          message='Voor dit bezorgmoment is geen menu gevonden.'
        />
      )}
      {allProducts.length > 0 && (
        <>
          <Row>
            <div style={{ float: 'right', marginRight: 10 }}>
              <label>Pas een standaardportiegrootte toe: </label>
              <Select<string>
                style={{ width: 220 }}
                placeholder={'- Maak een keuze -'}
                onChange={setDefaultPortionSize}
                value={defaultPortionSize || undefined}
              >
                <Select.Option value={'Small'}>Small (350g)</Select.Option>
                <Select.Option value={'Medium'}>Medium (450g)</Select.Option>
                <Select.Option value={'Large'}>Large (600g)</Select.Option>
              </Select>
            </div>
          </Row>
          <>
            <h3>Maaltijdkeuze</h3>
            <Form.Item {...dishLayout}>
              {allProducts.map((item, index) => {
                const hasOnePortion = item.portions.length === 1;
                const selectedKey = `weeklyMenuItems[${item.mealSequenceNumber}].selected`;
                const isBox = 'mainVariantBoxProductId' in item;
                const productId = isBox
                  ? (item as any).mainVariantBoxProductId
                  : (item as any).mainVariantProductId;
                const itemShouldShowAlternatives =
                  item.alternatives && item.alternatives.length > 0;

                return (
                  <React.Fragment key={index}>
                    <Row gutter={5}>
                      <Col span={16}>
                        <Form.Item style={{ marginBottom: 0 }}>
                          {form.getFieldDecorator(selectedKey, {
                            valuePropName: 'checked'
                          })(
                            <Checkbox>
                              <Tag color='#ff9800'>{item.mealSequenceNumber}</Tag>
                              &nbsp;{productId} - {item.name} &nbsp;&nbsp;
                            </Checkbox>
                          )}
                        </Form.Item>
                        <Form.Item style={{ display: 'none' }}>
                          {form.getFieldDecorator(
                            `weeklyMenuItems[${item.mealSequenceNumber}].category`,
                            { initialValue: item.category }
                          )(<Input type='hidden' />)}
                        </Form.Item>
                      </Col>
                      {form.getFieldValue(selectedKey) && (
                        <Col span={8}>
                          {item.portions.slice().map((portion, portionIndex) => {
                            let initialPortionValue: number | undefined = undefined;

                            const currentQuantity = form.getFieldValue(
                              `weeklyMenuItems_${item.mealSequenceNumber}_portions_${portion.portionSize}_quantity`
                            );
                            if (currentQuantity === undefined) {
                              // first time value is entered
                              if (portion.portionSize === defaultPortionSize || hasOnePortion) {
                                // with default portion selected and matching
                                initialPortionValue = 1; // set default to 1
                              } else {
                                initialPortionValue = 0; // otherwise set default to 0
                              }
                            } else {
                              initialPortionValue = currentQuantity; // when value is already entered keep the value
                            }

                            return (
                              <Row key={portionIndex} type='flex' justify='space-between'>
                                <Col span={14} style={{ textAlign: 'right' }}>
                                  {hasOnePortion ? 'Aantal' : portion.portionSize}
                                </Col>
                                <Col span={0}>
                                  <Form.Item>
                                    {form.getFieldDecorator(
                                      `weeklyMenuItems_${item.mealSequenceNumber}_portions_${portion.portionSize}`,
                                      { initialValue: portion.portionSize }
                                    )(<InputNumber type='hidden' />)}
                                  </Form.Item>
                                </Col>
                                <Col span={9}>
                                  <Form.Item style={{ marginBottom: 0 }}>
                                    {form.getFieldDecorator(
                                      `weeklyMenuItems[${item.mealSequenceNumber}].portions[${portion.portionSize}].quantity`,
                                      { initialValue: initialPortionValue }
                                    )(<InputNumber type='number' min={0} placeholder='Aantal' />)}
                                  </Form.Item>
                                </Col>
                                <Col span={0}>
                                  <Form.Item>
                                    {form.getFieldDecorator(
                                      `weeklyMenuItems[${item.mealSequenceNumber}].portions[${portion.portionSize}].productId`,
                                      { initialValue: portion.productId }
                                    )(<Input type='hidden' />)}
                                  </Form.Item>
                                </Col>
                              </Row>
                            );
                          })}
                        </Col>
                      )}
                      {itemShouldShowAlternatives && (
                        <div
                          style={{
                            position: 'absolute',
                            left: '10px',
                            bottom: '-21px',
                            width: '12px',
                            height: 'calc(100% - 15px)',
                            borderLeft: '1px solid #d9d9d9',
                            borderBottom: '1px solid #d9d9d9'
                          }}
                        />
                      )}
                    </Row>
                    {itemShouldShowAlternatives &&
                      (item.alternatives as any[]).map((alternative: any, altIndex: number) => {
                        const altSelectedKey = `alternativeItems[${item.mealSequenceNumber}][${alternative.productId}].selected`;
                        const isBoxAlternative =
                          alternative.portions && alternative.portions.length > 0;

                        // For regular alternatives (non-box)
                        const altQuantityKey = `alternativeItems[${item.mealSequenceNumber}][${alternative.productId}].quantity`;
                        const handleAlternativeChange = (e: any) => {
                          if (e.target.checked && !isBoxAlternative) {
                            // Only set quantity for regular alternatives, not box alternatives
                            setTimeout(() => {
                              form.setFieldsValue({ [altQuantityKey]: 1 });
                            }, 0);
                          }
                        };

                        return (
                          <Row gutter={5} key={`alt-${altIndex}`}>
                            <Col span={16}>
                              <Form.Item
                                style={{ marginBottom: 0, marginLeft: 26, position: 'relative' }}
                              >
                                {form.getFieldDecorator(altSelectedKey, {
                                  valuePropName: 'checked',
                                  initialValue: false
                                })(
                                  <Checkbox onChange={handleAlternativeChange}>
                                    <Tag color='#2196f3'>{alternative.mealSequenceNumber}</Tag>
                                    &nbsp;{alternative.productId} - {alternative.name}
                                    <div
                                      style={{
                                        fontSize: '12px',
                                        color: '#999999',
                                        marginLeft: '7vh',
                                        lineHeight: 'normal'
                                      }}
                                    >
                                      {alternative.alternativeText}
                                    </div>
                                  </Checkbox>
                                )}
                              </Form.Item>
                              <Form.Item style={{ display: 'none' }}>
                                {form.getFieldDecorator(
                                  `alternativeItems[${item.mealSequenceNumber}][${alternative.productId}].productId`,
                                  { initialValue: alternative.productId }
                                )(<Input type='hidden' />)}
                              </Form.Item>
                              <Form.Item style={{ display: 'none' }}>
                                {form.getFieldDecorator(
                                  `alternativeItems[${item.mealSequenceNumber}][${alternative.productId}].category`,
                                  { initialValue: item.category }
                                )(<Input type='hidden' />)}
                              </Form.Item>
                            </Col>
                            {form.getFieldValue(altSelectedKey) && (
                              <Col span={8}>
                                {isBoxAlternative ? (
                                  // Box alternative with portions (Small, Medium, Large)
                                  <>
                                    {alternative.portions.map(
                                      (portion: any, portionIndex: number) => (
                                        <Row key={portionIndex} type='flex' justify='space-between'>
                                          <Col span={14} style={{ textAlign: 'right' }}>
                                            {portion.portionSize}
                                          </Col>
                                          <Col span={9}>
                                            <Form.Item style={{ marginBottom: 0 }}>
                                              {form.getFieldDecorator(
                                                `alternativeItems[${item.mealSequenceNumber}][${alternative.productId}].portions[${portion.portionSize}].quantity`,
                                                { initialValue: 0 }
                                              )(
                                                <InputNumber
                                                  type='number'
                                                  min={0}
                                                  placeholder='Aantal'
                                                />
                                              )}
                                            </Form.Item>
                                          </Col>
                                          <Col span={0}>
                                            <Form.Item>
                                              {form.getFieldDecorator(
                                                `alternativeItems[${item.mealSequenceNumber}][${alternative.productId}].portions[${portion.portionSize}].productId`,
                                                { initialValue: portion.productId }
                                              )(<Input type='hidden' />)}
                                            </Form.Item>
                                          </Col>
                                        </Row>
                                      )
                                    )}
                                  </>
                                ) : (
                                  // Regular alternative with single quantity
                                  <Row type='flex' justify='space-between'>
                                    <Col span={14} style={{ textAlign: 'right' }}>
                                      Aantal
                                    </Col>
                                    <Col span={9}>
                                      <Form.Item style={{ marginBottom: 0 }}>
                                        {form.getFieldDecorator(altQuantityKey, {
                                          initialValue: 0
                                        })(
                                          <InputNumber type='number' min={0} placeholder='Aantal' />
                                        )}
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                )}
                              </Col>
                            )}
                            {altIndex !== 0 && (
                              <div
                                style={{
                                  position: 'absolute',
                                  left: '10px',
                                  bottom: '20px',
                                  width: '12px',
                                  height: '100%',
                                  borderLeft: '1px solid #d9d9d9',
                                  borderBottom: '1px solid #d9d9d9'
                                }}
                              />
                            )}
                          </Row>
                        );
                      })}
                  </React.Fragment>
                );
              })}
            </Form.Item>
          </>
        </>
      )}
      <Row>
        <Col>
          <Button onClick={onHideClick}>Annuleren</Button>
          <Button
            style={{ float: 'right' }}
            disabled={upsertCartDisabled}
            loading={isSubmitting || isUpserting}
            type='primary'
            onClick={handleUpsertCart}
          >
            Bestellen
          </Button>
        </Col>
      </Row>
    </Form>
  );
};
export { OrderFormComponent };
